import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useToggle } from 'hooks/useToggle';
import { useBoardData, useBoardActions } from '../context/BoardContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

export default function AddNewStage() {
  const { practiceAreaList, leadStages } = useBoardData();
  const { createLeadStage, checkStageExists } = useBoardActions();
  const { addToast } = useToast();
  const [displayPosition, setDisplayPosition] = useToggle(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm();

  const onHide = () => {
    reset();
    setDisplayPosition(false);
  };

  const handleStageCreate = async (params) => {
    // Calculate position for the new stage (before the last "Closed" stage)
    const position = leadStages && leadStages.length > 0 ? leadStages[leadStages.length - 2]?.position || 0 : 0;

    const data = {
      lead_stage_name: params.laneName.trim(),
      position: position + 1,
      practice_area: params.practice_area?.length > 0 ? params.practice_area : undefined,
    };

    const result = await createLeadStage(data);

    if (result.success) {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_STAGE_CREATED);
      onHide();
    } else {
      // Error is handled by the mutation, but we can show a generic error
      const errorMessage =
        result.error?.response?.data || toastConstant.message.LEAD_STAGE_CREATION_FAILED || 'Failed to create lead stage';
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMessage);
    }
  };

  return (
    <>
      <div id="addtask" className="add-new-stage-btn pointer p-2" onClick={setDisplayPosition}>
        <i className="icon-add" id="addtask" />
        <Tooltip position="left" target="#addtask" content={`Add new stage`} />
      </div>
      <Dialog
        header="Add Stage"
        visible={displayPosition}
        position="right"
        className="task-add-stage"
        footer={<></>}
        modal
        onHide={onHide}
        contentClassName="pt-1"
      >
        <div className="d-flex flex-column">
          <div className="d-flex flex-column mb-3">
            <Controller
              name="laneName"
              control={control}
              defaultValue=""
              rules={{ required: true, validate: (value) => !checkStageExists(value), pattern: { value: /^(?!^\d+$).*/i } }}
              render={({ field }) => (
                <TextSnippetWrapper placeholder="Enter Stage Name" type="text" className="input-shadow me-2 w-100" {...field} />
              )}
            />
            {errors?.laneName?.type === 'validate' ? <InputErrorMessage>This Stage Already Exists</InputErrorMessage> : null}

            {errors?.laneName?.type === 'required' ? <InputErrorMessage>Stage name is required</InputErrorMessage> : null}
            {errors?.laneName?.type === 'pattern' ? <InputErrorMessage>At least 1 character is required</InputErrorMessage> : null}
          </div>
          <div className="d-flex flex-column mb-3">
            <Controller
              name="practice_area"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  id={field.name}
                  onChange={(e) => field.onChange(e.value)}
                  value={field.value}
                  options={practiceAreaList?.filter((item) => item.status === 'ACTIVE') || []}
                  optionLabel="practice_area_name"
                  optionValue="sk"
                  className="col-sm-12 input-shadow w-100"
                  placeholder="Select Practice Area"
                  filter
                  display="chip"
                />
              )}
            />
          </div>
          <div className="d-flex justify-content-center align-items-center mx-1 flex-row">
            <Button label="Save" className="p-button p-button-secondary w-100" onClick={handleSubmit(handleStageCreate)} autoFocus />
            <Button label="Cancel" className="p-button p-button-secondary outline ms-2 w-100" onClick={onHide} />
          </div>
        </div>
      </Dialog>
    </>
  );
}
